/**
 * useMemorySnapArrivals — shared "new-snap detector + stagger queue"
 * for the Memory Wall Hero Moment. Used by MemoryWallPage,
 * VenueLivePage, and (optionally) the host's preview tile.
 *
 * Per product direction:
 *   • when 1 new snap arrives → it goes through the hero choreography
 *     immediately
 *   • when 2+ arrive together → they form a stagger queue with ~1.5s
 *     gaps so the projector never plays simultaneous hero moments
 *     (which read as "social-feed activity", not "cinematic atmosphere")
 *   • only ONE snap is the active hero at a time — `heroSnap` is the
 *     one currently mid-animation. `allSnaps` keeps the full ordered
 *     list for the underlying wall surface.
 *
 * Initial hydration is silent — no hero moment fires for snaps that
 * already exist when the surface first mounts. Only snaps arriving
 * AFTER the first successful fetch trigger the choreography.
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const absoluteUrl = (u) => (u && !u.startsWith('http') ? `${BACKEND_URL}${u}` : u);

// Default hero animation lifetime per intensity — used to dequeue the
// next stagger entry once the current hero has fully cleared.
export const HERO_LIFETIME_MS = {
  tile:      2_400,   // restrained — already small on the host tile
  wall:      4_600,   // 0.6 rise + 0.8 tape + 1.0 settle + ~2.2 glow
  projector: 6_400,   // 1.0 rise + 1.0 tape + 1.4 settle + ~3.0 glow
};

// Inter-hero stagger gap — second new snap starts its hero animation
// this long after the previous one began. 1500ms keeps each beat
// distinct without dragging.
const STAGGER_GAP_MS = 1_500;

export function useMemorySnapArrivals(eventId, opts = {}) {
  const {
    intensity = 'wall',           // 'tile' | 'wall' | 'projector'
    pollMs = 8_000,
    limit = 60,
    enabled = true,
  } = opts;

  const [allSnaps, setAllSnaps] = useState([]);
  const [heroSnap, setHeroSnap] = useState(null);

  const knownIdsRef = useRef(new Set());
  const queueRef = useRef([]);        // [{snap, scheduledAt}]
  const heroTimerRef = useRef(null);
  const hydratedRef = useRef(false);
  const heroLifetime = HERO_LIFETIME_MS[intensity] || HERO_LIFETIME_MS.wall;

  // Schedule a queue drain — called whenever a new snap is enqueued
  // OR the current hero finishes. Always single-flight.
  const drainQueue = () => {
    if (heroTimerRef.current) return;     // hero in flight, will retry on completion
    const next = queueRef.current.shift();
    if (!next) return;
    const startAt = Date.now();
    setHeroSnap({ ...next.snap, _heroStartedAt: startAt });
    heroTimerRef.current = setTimeout(() => {
      setHeroSnap(null);
      heroTimerRef.current = null;
      // Schedule next entry after the stagger gap (measured from
      // when the previous one *started* — so the gap stays consistent
      // even if hero lifetime varies).
      const elapsed = Date.now() - startAt;
      const wait = Math.max(0, STAGGER_GAP_MS - Math.max(0, elapsed - heroLifetime));
      if (queueRef.current.length) setTimeout(drainQueue, wait);
    }, heroLifetime);
  };

  useEffect(() => {
    if (!enabled || !eventId) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/memory-snaps?limit=${limit}`);
        if (cancelled) return;
        const incoming = (r.data?.snaps || []).map((s) => ({
          ...s, photo_url: absoluteUrl(s.photo_url),
        }));
        // Wall surfaces want chronological (oldest → newest) so the
        // wall grows downward over time. Projector surfaces consume
        // the array directly so we leave ordering to the caller via
        // the returned `allSnaps` (default DESC). Sort once here so
        // both consumers can rely on a single ordering.
        const sorted = [...incoming].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        setAllSnaps(sorted);

        // Detect arrivals — only AFTER first hydration, otherwise
        // we'd fire hero moments for every existing snap on mount.
        if (hydratedRef.current) {
          const fresh = incoming.filter((s) => !knownIdsRef.current.has(s.snap_id));
          if (fresh.length) {
            // Newest-first ordering for the stagger queue — the most
            // recent arrival should be the first hero moment.
            const ordered = [...fresh].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            ordered.forEach((s) => queueRef.current.push({ snap: s, scheduledAt: Date.now() }));
            drainQueue();
          }
        }
        incoming.forEach((s) => knownIdsRef.current.add(s.snap_id));
        if (!hydratedRef.current) hydratedRef.current = true;
      } catch { /* quiet — surface stays in last good state */ }
    };
    pull();
    const iv = setInterval(pull, pollMs);
    return () => {
      cancelled = true;
      clearInterval(iv);
      if (heroTimerRef.current) { clearTimeout(heroTimerRef.current); heroTimerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, enabled, pollMs, limit]);

  // Convenience selectors for consumers that want "everything except
  // the active hero" — used to dim surrounding wall content during
  // the glow linger so the new memory becomes the focal point.
  const otherSnaps = useMemo(
    () => (heroSnap ? allSnaps.filter((s) => s.snap_id !== heroSnap.snap_id) : allSnaps),
    [allSnaps, heroSnap]
  );

  return { allSnaps, heroSnap, otherSnaps, isHeroActive: !!heroSnap, heroLifetime };
}
